/**
 * SUB-5.3 · FR48. M2, M3 and M4, as arithmetic.
 *
 * M1 is in `portfolioRollup.ts` because SUB-5.1 wired it to prove the read path.
 * This file adds the other three. `PortfolioRollup.tsx` lays all four out and
 * adds nothing to any of them.
 *
 * ⚠️ THIS DECIDES WHAT IS SHOWN. IT BOUNDS NOTHING. The accounts these metrics
 * cover is `merged_scope_tenant_ids()` evaluated inside `projects_select` and
 * `tasks_select`, both of which SUB-4.8 already widened. Nothing here may ever
 * be why a row is absent.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ THE READ PATH IS DIFFERENT FROM M1's, AND THAT IS NOT AN INCONSISTENCY.
 *
 * M1 needs a gated `SECURITY DEFINER` function because it reads
 * `stages.sla_settings`, and `stages_select` is single-tenant, so a join returns
 * NULL for every sub-account (F-50, brief §3). M2, M3 and M4 read only columns
 * of `projects` and `tasks`, whose SELECT policies already resolve through
 * merged scope. They need no function, and adding one would be a second
 * authorization surface to keep in step with a policy for no gain.
 *
 * ---------------------------------------------------------------------------
 * THE DECISIONS THIS FILE IMPLEMENTS
 *
 * | | | |
 * |---|---|---|
 * | **D53** | Q14 | M2 reports the **absolute** count with a configurable window, and the RANKING stays relative. §1 |
 * | **D54** | Q11 | M3's "open work" is **tasks**, not projects by assigned role. §2 |
 * | **D55** | Q12 | `deleted_at IS NULL` is part of every metric's population, not a separate filter. §3 |
 * | **D56** | — | M2's window lives in the **URL**, defaulting to 30 days. §1 |
 * | **D57** | — | M4 reports **active now and new in 30 days**. The trend FR48 asks for is **not computable** and the page says so rather than approximating it. §4 |
 *
 * ---------------------------------------------------------------------------
 * §1 · M2, AND WHY THE NUMBER IS ALLOWED TO BE UGLY
 *
 * Measured live 2026-08-16 over 4,823 live projects:
 *
 * | | Projects | Share |
 * |---|---|---|
 * | Moved stage in the last 30 days | **195** | 4% |
 * | …60 days | 660 | 14% |
 * | …90 days | 1,400 | 29% |
 * | **Median days in current stage** | **254** | |
 * | p90 days in current stage | 440 | |
 *
 * So M2 at the FR's implied 30 days flags **96%** of the portfolio, and raising
 * the window does not rescue it: 90 days still flags 71%. The data is not
 * broken. Only **773** projects were touched at all in 30 days, and every status
 * other than `active` has been touched **zero** times. Most of this portfolio is
 * abandoned records rather than late work.
 *
 * **D53: the count stays absolute and true, and the account ranking stays
 * relative.** The page therefore always says where to start even when every
 * absolute number is alarming, which is the same shape SUB-5.1 already used for
 * M1 rather than a new idea. ⚠️ The alternative, flagging the worst decile per
 * account, was refused because it would permanently hide the systemic fact: there
 * is always a worst 10%, however healthy the portfolio becomes.
 *
 * **D56: the window is a URL parameter, default 30.** FR48 says settable, not
 * hardcoded; it does not say persisted. A URL is shareable, needs no schema, and
 * is consistent with D51 giving up server-side layout persistence deliberately.
 * `tenant_settings` and a per-user preference were both considered and both cost
 * a write path for a number Dana changes while looking at the page.
 *
 * §2 · M3 IS TASKS
 *
 * Measured: 176 tasks, **91 open**, 20 distinct assignees, against 219 distinct
 * `assigned_sales_rep_id` holders each carrying ~22 projects. 91 items across 20
 * people is a workload someone can act on; 219 people holding projects is a
 * directory. Tasks also carry a real completion state, so "open" means something,
 * where a project assignment does not end.
 *
 * ⚠️ **Thin, and honestly so.** Tasks are used in 5 of 13 accounts. M3 will be
 * empty for most clients, so the surface reports the accounts that contributed
 * and an empty leaderboard is never mistaken for an idle team.
 *
 * ⚠️ **M3 IS NO LONGER COMPUTED HERE.** D99 replaced this file's `teamLoad()`
 * with `portfolioSignal.ts:openTasksByAccount` plus the
 * `merged_scope_task_load_by_person` view; SUB-5.23 removed the last caller and
 * SUB-5.25 removed the function. The rationale above still holds and is why the
 * metric is tasks rather than project assignment.
 *
 * ⚠️ **D50: a sub-account's staff cannot be NAMED.** `users_select` is
 * single-tenant, so in merged scope it returns the anchor's people and nothing
 * else. Own-account assignees are named; everyone else is grouped as their
 * account. A leaderboard of bare uuids would be worse than a grouped one.
 *
 * §3 · SOFT DELETES, AND THERE ARE TWO MECHANISMS
 *
 * `deleted_at` is non-null on **149** projects. Separately, `project_status`
 * canonicalises to `deleted` on **115**. ⚠️ **They are not the same set and
 * nothing keeps them in step.** `widget_count_grouped` filters `deleted_at IS
 * NULL` and every metric here does the same, so the two agree on the column that
 * is actually authoritative. The status bucket is left alone: folding it in would
 * make this file the third opinion about what deleted means.
 *
 * §4 · M4's TREND CANNOT BE COMPUTED, AND APPROXIMATING IT WOULD BE WORSE
 *
 * FR48 asks for "active projects per account with a 30-day trend". A trend needs
 * the active count as it was 30 days ago, and **nothing records status history**:
 * `project_stage_history` tracks STAGE, not status, and `projects.project_status`
 * has no audit trail. The count 30 days ago is not recoverable from the live
 * database at any cost.
 *
 * **D57: report what is true.** Active now, and new in the last 30 days from
 * `created_at`, labelled as new rather than as a trend. A subtraction dressed up
 * as a delta would be a number nobody could defend, and this epic has spent three
 * stories removing figures like that.
 */

import { canonicalProjectStatus } from './projectStatusVocabulary';

/** The columns M2 and M4 need. All of them are on `projects` itself. */
export interface PortfolioProjectRow {
  /**
   * SUB-5.14 · D90. ⚠️ ADDED SO A FIGURE CAN HAND ITS DRILL-DOWN THE EXACT ROWS
   * IT COUNTED, rather than a predicate the far side re-evaluates. The whole
   * class of defect SUB-5.90 closed was a figure and its list disagreeing; ids
   * make that impossible by construction. Nothing here reads it: the metric
   * functions still count, and `portfolioFigureProjects.ts` selects.
   */
  id: string;
  tenant_id: string;
  /** SUB-5.15. The triage queue names the project, so a row is actionable. */
  project_name: string | null;
  /**
   * SUB-5.15. Which stage the project is sitting in, so a blind spot can be
   * attributed to the stage that is missing an SLA rather than to the account.
   * "Solerro has 15% coverage" is a complaint; "Solerro · Permitting holds 1,204
   * projects and has no SLA" is a thing someone can go and fix.
   */
  current_stage_id: number | null;
  project_status: string | null;
  stage_changed_at: string | null;
  created_at: string | null;
  deleted_at: string | null;
}

/** The columns M3 needs. `tasks_select` already resolves through merged scope. */
export interface PortfolioTaskRow {
  tenant_id: string;
  assignee_id: string | null;
  status: string | null;
  completed_at: string | null;
}

export const DEFAULT_STALLED_DAYS = 30;

/**
 * D56. The window, read from a URL parameter and clamped to something a chart
 * can render.
 *
 * ⚠️ Fails to the default rather than to zero. A zero window would flag every
 * project including ones that moved this morning, which reads as a catastrophe
 * caused by a typo in a query string.
 */
export function stalledWindowDays(raw: string | null | undefined): number {
  if (raw === null || raw === undefined || raw.trim() === '') return DEFAULT_STALLED_DAYS;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return DEFAULT_STALLED_DAYS;
  const whole = Math.floor(parsed);
  if (whole < 1) return DEFAULT_STALLED_DAYS;
  return Math.min(whole, 3650);
}

/** Days since this project last changed stage, or null if that cannot be read. */
export function daysSinceStageChange(
  row: PortfolioProjectRow,
  now: Date = new Date(),
): number | null {
  if (!row.stage_changed_at) return null;
  const then = new Date(row.stage_changed_at).getTime();
  if (!Number.isFinite(then)) return null;
  return Math.floor((now.getTime() - then) / 86_400_000);
}

/**
 * D55. Every metric's population: not soft-deleted.
 *
 * ⚠️ On `deleted_at` alone. See §3: a second, disagreeing soft-delete lives in
 * `project_status`, and this file deliberately does not arbitrate between them.
 */
export function isCounted(row: PortfolioProjectRow): boolean {
  return row.deleted_at === null || row.deleted_at === undefined;
}

export interface StalledMetric {
  /** Projects counted, after the soft-delete filter. */
  population: number;
  /** Of those, with no stage movement inside the window. */
  stalled: number;
  /** ⚠️ Counted, but `stage_changed_at` could not be read. Neither, not "moved". */
  unknown: number;
  windowDays: number;
  /** Per account, for the ranking that does the discriminating. */
  byAccount: Map<string, { stalled: number; population: number; oldestDays: number | null }>;
}

/**
 * M2. No stage movement in `windowDays`.
 *
 * ⚠️ THREE STATES AGAIN. A project whose `stage_changed_at` is null has an
 * unreadable clock, and calling that "moved recently" would quietly shrink the
 * number. It is counted as unknown, the same discipline `merged_scope_project_sla`
 * applies to M1 and for the same reason (NFR8).
 */
export function stalledProjects(
  rows: readonly PortfolioProjectRow[],
  windowDays: number,
  now: Date = new Date(),
): StalledMetric {
  const byAccount = new Map<string, { stalled: number; population: number; oldestDays: number | null }>();
  let population = 0;
  let stalled = 0;
  let unknown = 0;

  for (const row of rows) {
    if (!isCounted(row)) continue;
    population += 1;

    let acc = byAccount.get(row.tenant_id);
    if (!acc) {
      acc = { stalled: 0, population: 0, oldestDays: null };
      byAccount.set(row.tenant_id, acc);
    }
    acc.population += 1;

    const days = daysSinceStageChange(row, now);
    if (days === null) {
      unknown += 1;
      continue;
    }
    if (acc.oldestDays === null || days > acc.oldestDays) acc.oldestDays = days;
    if (days >= windowDays) {
      stalled += 1;
      acc.stalled += 1;
    }
  }

  return { population, stalled, unknown, windowDays, byAccount };
}

export interface ActiveMetric {
  /** Canonicalised to the `active` bucket, and not soft-deleted. */
  active: number;
  /** Created inside the last 30 days. ⚠️ NEW, not a trend. See §4. */
  newIn30Days: number;
  byAccount: Map<string, { active: number; newIn30Days: number }>;
}

/**
 * M4. Active projects per account, and how many are new.
 *
 * ⚠️ "Active" is the canonical bucket from `projectStatusVocabulary.ts`, matched
 * case-insensitively, never a raw `project_status` equality. Nine spellings exist
 * across nine accounts and five of them appear in exactly one, which is F-45. An
 * equality filter on one spelling silently drops whole accounts.
 */
export function activeProjects(
  rows: readonly PortfolioProjectRow[],
  now: Date = new Date(),
): ActiveMetric {
  const cutoff = now.getTime() - 30 * 86_400_000;
  const byAccount = new Map<string, { active: number; newIn30Days: number }>();
  let active = 0;
  let newIn30Days = 0;

  for (const row of rows) {
    if (!isCounted(row)) continue;
    if (canonicalProjectStatus(row.project_status) !== 'active') continue;

    active += 1;
    let acc = byAccount.get(row.tenant_id);
    if (!acc) {
      acc = { active: 0, newIn30Days: 0 };
      byAccount.set(row.tenant_id, acc);
    }
    acc.active += 1;

    if (row.created_at) {
      const created = new Date(row.created_at).getTime();
      if (Number.isFinite(created) && created >= cutoff) {
        newIn30Days += 1;
        acc.newIn30Days += 1;
      }
    }
  }

  return { active, newIn30Days, byAccount };
}
